import { formatCurrency } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyTableModule } from '@angular/material/legacy-table';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { RecruitingDealsService } from 'src/app/recruiting/deals/deals.service';
import { OffersUserType } from 'src/app/shared/models/enums/offers.enum';
import { OfferMarginDifference } from 'src/app/shared/offers-shared/offer-details/dialog-offer-margin-differences/model/offer-margin-difference.model';
import { SharedOffersService as OfferService } from 'src/app/shared/offers-shared/offers.service';

@UntilDestroy()
@Component({
    selector: 'nova-offer-margin-differences-dialog',
    templateUrl: './offer-margin-differences-dialog.component.html',
    styleUrls: ['./offer-margin-differences-dialog.component.scss'],
    standalone: true,
    imports: [MatLegacyButtonModule, MatDialogModule, MatIconModule, MatLegacyTableModule]
})
export class OfferMarginDifferencesDialogComponent implements OnInit {
    displayedColumns: string[] = ['propertyName', 'valueInOffer', 'valueInMargin'];
    offerDifferences$: Observable<OfferMarginDifference[]>;
    differences: OfferMarginDifference[] = [];

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            userType: OffersUserType;
            offerId: number;
            marginId: number;
            differences: OfferMarginDifference[];
            candidateName: string;
        },
        @Inject(LOCALE_ID) private readonly locale: string,
        private readonly _recruitingDealsService: RecruitingDealsService,
        private readonly _offersService: OfferService
    ) {}

    ngOnInit(): void {
        this.differences = this.formatOfferMarginDifferences(this.data.differences);
        this.reloadOfferMarginDifferences();
    }

    reloadOfferMarginDifferences() {
        if (this.data.userType === OffersUserType.Recruiter) {
            this.offerDifferences$ = this._recruitingDealsService.getOfferMarginDifferences(this.data.offerId);
        } else {
            this.offerDifferences$ = this._offersService.getOfferMarginDifferences(this.data.offerId);
        }

        this.offerDifferences$.pipe(untilDestroyed(this)).subscribe((response) => {
            if (response) {
                this.differences = this.formatOfferMarginDifferences(response);
            }
        });
    }

    formatOfferMarginDifferences(differences: OfferMarginDifference[]) {
        const formattedDiff: OfferMarginDifference[] = [];
        if (differences) {
            differences.forEach((x) => {
                const propName: string = x.propertyName?.toUpperCase();
                const offerMarginDiff = { ...x };
                switch (propName) {
                    case 'CANDIDATENAME':
                        offerMarginDiff.propertyName = 'Candidate';
                        break;
                    case 'CONTRACTTYPE':
                        offerMarginDiff.propertyName = 'Contract Type';
                        break;
                    case 'FACILITYNAME':
                        offerMarginDiff.propertyName = 'Facility';
                        break;
                    case 'ACCOUNTMANAGERNAME':
                        offerMarginDiff.propertyName = 'AM';
                        break;
                    case 'JOBID':
                        offerMarginDiff.propertyName = 'Job Id';
                        break;
                    case 'PROFESSIONNAME':
                        offerMarginDiff.propertyName = 'Candidate Profession';
                        break;
                    case 'EXPERTISENAME':
                        offerMarginDiff.propertyName = 'Job Specialty';
                        break;
                    case 'CONTRACTLENGTHTEXT':
                        offerMarginDiff.propertyName = 'Contract Length';
                        break;
                    case 'STARTDATETEXT':
                        offerMarginDiff.propertyName = 'Start';
                        break;
                    case 'ENDDATETEXT':
                        offerMarginDiff.propertyName = 'End';
                        break;
                    case 'SHIFTTEXT':
                        offerMarginDiff.propertyName = 'Shifts';
                        break;
                    case 'RTOTEXT':
                        offerMarginDiff.propertyName = 'RTO Dates';
                        break;
                    case 'ISRTOAPPROVED':
                        offerMarginDiff.propertyName = 'RTO Approval';
                        offerMarginDiff.valueInOffer = x.valueInOffer ? 'Approved' : 'Not Approved';
                        offerMarginDiff.valueInMargin = x.valueInMargin ? 'Approved' : 'Not Approved';
                        break;
                    case 'BILLINGRATE':
                        offerMarginDiff.propertyName = 'Billing Rate';
                        offerMarginDiff.valueInOffer = formatCurrency(x.valueInOffer, this.locale, '$');
                        offerMarginDiff.valueInMargin = formatCurrency(x.valueInMargin, this.locale, '$');
                        break;
                    default:
                        break;
                }
                formattedDiff.push(offerMarginDiff);
            });
        }
        return formattedDiff;
    }
}
